import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ChangeEvent,
  type FormEvent,
} from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { downloadCsv, parseCsv } from "../lib/csv";
import { useOrganization } from "../organization/OrganizationProvider";

type WarehouseOption = {
  id: string;
  name: string;
  code: string;
  address: string | null;
};

type ItemOption = {
  id: string;
  sku: string | null;
  name: string;
  unit: string;
  cost_price: number | string;
  sale_price: number | string;
  tax_rate: number | string;
};

type InventoryRow = {
  warehouse_id: string;
  warehouse_name: string;
  warehouse_code: string;
  catalog_item_id: string;
  sku: string | null;
  item_name: string;
  unit: string;
  quantity: number | string;
  reserved_quantity: number | string;
  available_quantity: number | string;
  minimum_quantity: number | string;
  average_cost: number | string;
  stock_value: number | string;
  low_stock: boolean;
  updated_at: string | null;
};

type ReorderSuggestion = {
  catalog_item_id: string;
  sku: string | null;
  item_name: string;
  warehouse_id: string;
  warehouse_code: string;
  quantity: number | string;
  reserved_quantity: number | string;
  available_quantity: number | string;
  minimum_quantity: number | string;
  suggested_quantity: number | string;
  supplier_id: string | null;
  supplier_name: string | null;
  supplier_price: number | string | null;
  currency_code: string | null;
  lead_time_days: number | null;
  preferred: boolean | null;
};

type MovementType =
  | "opening"
  | "purchase"
  | "return_in"
  | "return_out"
  | "adjustment_in"
  | "adjustment_out";

const MOVEMENT_LABELS: Record<MovementType, string> = {
  opening: "Stock inicial",
  purchase: "Compra / ingreso",
  return_in: "Devolución de entrada",
  return_out: "Devolución de salida",
  adjustment_in: "Ajuste de entrada",
  adjustment_out: "Ajuste de salida",
};

function number(value: number | string) {
  return Number(value || 0);
}

function money(value: number | string) {
  return new Intl.NumberFormat("es-PE", {
    style: "currency",
    currency: "PEN",
  }).format(number(value));
}

export function InventoryPage() {
  const { selectedOrganization } = useOrganization();
  const organizationId = selectedOrganization?.organizationId ?? null;

  const [rows, setRows] = useState<InventoryRow[]>([]);
  const [warehouses, setWarehouses] = useState<WarehouseOption[]>([]);
  const [items, setItems] = useState<ItemOption[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [formOpen, setFormOpen] = useState(false);
  const [warehouseId, setWarehouseId] = useState("");
  const [itemId, setItemId] = useState("");
  const [movementType, setMovementType] =
    useState<MovementType>("adjustment_in");
  const [quantity, setQuantity] = useState("1");
  const [unitCost, setUnitCost] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);
  const [minimumInputs, setMinimumInputs] = useState<Record<string, string>>({});
  const [minimumBusyKey, setMinimumBusyKey] = useState<string | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [reorderSuggestions, setReorderSuggestions] = useState<ReorderSuggestion[]>([]);
  const [quoteBusyKey, setQuoteBusyKey] = useState<string | null>(null);

  const loadInventory = useCallback(
    async (query: string) => {
      if (!organizationId) {
        setRows([]);
        setWarehouses([]);
        setItems([]);
        setReorderSuggestions([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      setLoadError(null);

      const [overviewResult, warehousesResult, itemsResult, reorderResult] =
        await Promise.all([
          supabase.rpc("list_inventory_overview", {
            p_organization_id: organizationId,
            p_search: query.trim() || null,
          }),
          supabase.rpc("list_inventory_warehouses", {
            p_organization_id: organizationId,
          }),
          supabase.rpc("list_inventory_items", {
            p_organization_id: organizationId,
          }),
          supabase.rpc("list_inventory_reorder_suggestions", {
            p_organization_id: organizationId,
          }),
        ]);

      const firstError =
        overviewResult.error ??
        warehousesResult.error ??
        itemsResult.error ??
        reorderResult.error;

      if (firstError) {
        setLoadError(firstError.message);
      }

      setRows(
        overviewResult.error ? [] : ((overviewResult.data ?? []) as InventoryRow[]),
      );
      setWarehouses(
        warehousesResult.error
          ? []
          : ((warehousesResult.data ?? []) as WarehouseOption[]),
      );
      setItems(
        itemsResult.error ? [] : ((itemsResult.data ?? []) as ItemOption[]),
      );
      setReorderSuggestions(
        reorderResult.error
          ? []
          : ((reorderResult.data ?? []) as ReorderSuggestion[]),
      );
      setLoading(false);
    },
    [organizationId],
  );

  useEffect(() => {
    const timer = window.setTimeout(() => {
      void loadInventory(search);
    }, 250);

    return () => window.clearTimeout(timer);
  }, [loadInventory, search]);

  useEffect(() => {
    setSearch("");
    setWarehouseId("");
    setItemId("");
    setMovementType("adjustment_in");
    setQuantity("1");
    setUnitCost("");
    setNotes("");
    setActionError(null);
    setActionSuccess(null);
    setFormOpen(false);
    setMinimumInputs({});
  }, [organizationId]);

  useEffect(() => {
    if (!warehouseId && warehouses[0]) {
      setWarehouseId(warehouses[0].id);
    }
  }, [warehouseId, warehouses]);

  useEffect(() => {
    if (!itemId && items[0]) {
      setItemId(items[0].id);
      setUnitCost(String(items[0].cost_price ?? ""));
    }
  }, [itemId, items]);

  const summary = useMemo(() => {
    const totalValue = rows.reduce(
      (total, row) => total + number(row.stock_value),
      0,
    );
    const lowStock = rows.filter((row) => row.low_stock).length;
    const available = rows.reduce(
      (total, row) => total + number(row.available_quantity),
      0,
    );

    return {
      totalValue,
      lowStock,
      available,
    };
  }, [rows]);

  async function handleMovement(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!organizationId || !warehouseId || !itemId) {
      setActionError("Selecciona almacén y producto.");
      return;
    }

    const parsedQuantity = Number(quantity);
    const parsedCost = unitCost.trim() === "" ? null : Number(unitCost);

    if (!Number.isFinite(parsedQuantity) || parsedQuantity <= 0) {
      setActionError("La cantidad debe ser mayor a cero.");
      return;
    }

    if (
      parsedCost !== null &&
      (!Number.isFinite(parsedCost) || parsedCost < 0)
    ) {
      setActionError("El costo unitario no es válido.");
      return;
    }

    setSaving(true);
    setActionError(null);
    setActionSuccess(null);

    const { error } = await supabase.rpc(
      "register_manual_inventory_movement",
      {
        p_organization_id: organizationId,
        p_warehouse_id: warehouseId,
        p_catalog_item_id: itemId,
        p_movement_type: movementType,
        p_quantity: parsedQuantity,
        p_unit_cost: parsedCost,
        p_notes: notes.trim() || null,
      },
    );

    setSaving(false);

    if (error) {
      setActionError(error.message);
      return;
    }

    setQuantity("1");
    setNotes("");
    setActionSuccess("Movimiento registrado correctamente.");
    await loadInventory(search);
  }

  function downloadInventoryTemplate() {
    downloadCsv(
      "plantilla-inventario.csv",
      [
        "warehouse_code",
        "sku",
        "movement_type",
        "quantity",
        "unit_cost",
        "minimum_quantity",
        "notes",
      ],
      [
        {
          warehouse_code: warehouses[0]?.code ?? "ALM-01",
          sku: items[0]?.sku ?? "SKU-001",
          movement_type: "opening",
          quantity: "10",
          unit_cost: "50",
          minimum_quantity: "5",
          notes: "Carga inicial",
        },
      ],
    );
  }

  function exportInventory() {
    downloadCsv(
      "inventario-actual.csv",
      [
        "warehouse_code",
        "warehouse_name",
        "sku",
        "item_name",
        "quantity",
        "reserved_quantity",
        "available_quantity",
        "minimum_quantity",
        "average_cost",
        "stock_value",
        "low_stock",
      ],
      rows.map((row) => ({
        warehouse_code: row.warehouse_code,
        warehouse_name: row.warehouse_name,
        sku: row.sku ?? "",
        item_name: row.item_name,
        quantity: row.quantity,
        reserved_quantity: row.reserved_quantity,
        available_quantity: row.available_quantity,
        minimum_quantity: row.minimum_quantity,
        average_cost: row.average_cost,
        stock_value: row.stock_value,
        low_stock: row.low_stock ? "SI" : "NO",
      })),
    );
  }

  async function handleInventoryCsv(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !organizationId) return;

    setBulkBusy(true);
    setActionError(null);
    setActionSuccess(null);

    const csvRows = parseCsv(await file.text());
    let processed = 0;
    const errors: string[] = [];

    for (const [index, row] of csvRows.entries()) {
      const warehouse = warehouses.find(
        (item) => item.code.toLowerCase() === String(row.warehouse_code || "").toLowerCase(),
      );
      const item = items.find(
        (option) => (option.sku ?? "").toLowerCase() === String(row.sku || "").toLowerCase(),
      );
      const qty = Number(row.quantity || 0);
      const cost = row.unit_cost?.trim() ? Number(row.unit_cost) : null;
      const minimum = row.minimum_quantity?.trim()
        ? Number(row.minimum_quantity)
        : null;
      const movement = (row.movement_type || "adjustment_in") as MovementType;

      if (!warehouse || !item || !Number.isFinite(qty) || qty <= 0) {
        errors.push(`Fila ${index + 2}: almacén/SKU/cantidad inválidos.`);
        continue;
      }

      const { error: movementError } = await supabase.rpc(
        "register_manual_inventory_movement",
        {
          p_organization_id: organizationId,
          p_warehouse_id: warehouse.id,
          p_catalog_item_id: item.id,
          p_movement_type: movement,
          p_quantity: qty,
          p_unit_cost: cost !== null && Number.isFinite(cost) ? cost : null,
          p_notes: row.notes?.trim() || "Carga masiva CSV",
        },
      );

      if (movementError) {
        errors.push(`Fila ${index + 2}: ${movementError.message}`);
        continue;
      }

      if (minimum !== null && Number.isFinite(minimum) && minimum >= 0) {
        const { error: minimumError } = await supabase.rpc(
          "set_inventory_minimum_quantity",
          {
            p_organization_id: organizationId,
            p_warehouse_id: warehouse.id,
            p_catalog_item_id: item.id,
            p_minimum_quantity: minimum,
          },
        );
        if (minimumError) {
          errors.push(`Fila ${index + 2}: mínimo no actualizado: ${minimumError.message}`);
        }
      }

      processed += 1;
    }

    setBulkBusy(false);
    setActionSuccess(
      errors.length
        ? `Carga terminada: ${processed} filas aplicadas, ${errors.length} incidencias.`
        : `Carga masiva completada: ${processed} filas aplicadas.`,
    );
    if (errors.length) setActionError(errors.slice(0, 5).join(" "));
    await loadInventory(search);
  }

  async function requestSupplierQuote(item: ReorderSuggestion) {
    if (!organizationId || !item.supplier_id) {
      setActionError("Asocia un proveedor al SKU antes de solicitar cotización.");
      return;
    }

    const key = item.warehouse_id + ":" + item.catalog_item_id;
    setQuoteBusyKey(key);
    setActionError(null);
    setActionSuccess(null);

    const { error } = await supabase.rpc("create_supplier_quote_request", {
      p_organization_id: organizationId,
      p_supplier_id: item.supplier_id,
      p_catalog_item_id: item.catalog_item_id,
      p_warehouse_id: item.warehouse_id,
      p_requested_quantity: Number(item.suggested_quantity),
      p_notes: "Generada desde alerta de reposición de inventario.",
    });

    setQuoteBusyKey(null);

    if (error) {
      setActionError(error.message);
      return;
    }

    setActionSuccess("Solicitud de cotización creada para el proveedor.");
  }

  async function saveMinimum(row: InventoryRow) {
    if (!organizationId) return;

    const key = `${row.warehouse_id}:${row.catalog_item_id}`;
    const raw = minimumInputs[key] ?? String(row.minimum_quantity);
    const value = Number(raw);

    if (!Number.isFinite(value) || value < 0) {
      setActionError("El stock mínimo no es válido.");
      return;
    }

    setMinimumBusyKey(key);
    setActionError(null);
    setActionSuccess(null);

    const { error } = await supabase.rpc(
      "set_inventory_minimum_quantity",
      {
        p_organization_id: organizationId,
        p_warehouse_id: row.warehouse_id,
        p_catalog_item_id: row.catalog_item_id,
        p_minimum_quantity: value,
      },
    );

    setMinimumBusyKey(null);

    if (error) {
      setActionError(error.message);
      return;
    }

    setActionSuccess("Stock mínimo actualizado.");
    await loadInventory(search);
  }

  if (!selectedOrganization) {
    return (
      <div className="page">
        <section className="empty-state">
          <strong>Selecciona una empresa.</strong>
          <p>Necesitas una empresa activa para trabajar con inventario.</p>
        </section>
      </div>
    );
  }

  return (
    <div className="page">
      <header className="page-header page-header-actions">
        <div>
          <span className="eyebrow">Inventario</span>
          <h1>{selectedOrganization.name}</h1>
          <p>Stock disponible, mínimos y movimientos por almacén.</p>
        </div>

        <div className="page-header-buttons">
          <button className="ghost-button" type="button" onClick={exportInventory}>
            Exportar CSV
          </button>
          <button className="ghost-button" type="button" onClick={downloadInventoryTemplate}>
            Descargar plantilla
          </button>
          <label className="ghost-button file-action-button">
            {bulkBusy ? "Importando…" : "Carga masiva CSV"}
            <input
              type="file"
              accept=".csv,text/csv"
              disabled={bulkBusy}
              onChange={(event) => void handleInventoryCsv(event)}
            />
          </label>
          <button
            className="primary-button"
            type="button"
            onClick={() => {
              setFormOpen((value) => !value);
              setActionError(null);
              setActionSuccess(null);
            }}
          >
            {formOpen ? "Cerrar" : "Registrar movimiento"}
          </button>
        </div>
      </header>

      <section className="inventory-summary-grid">
        <div className="metric-card">
          <span>Disponible total</span>
          <strong>{loading ? "…" : summary.available.toFixed(3)}</strong>
        </div>
        <div className="metric-card">
          <span>Alertas de mínimo</span>
          <strong>{loading ? "…" : summary.lowStock}</strong>
        </div>
        <div className="metric-card">
          <span>Valor de stock</span>
          <strong>{loading ? "…" : money(summary.totalValue)}</strong>
        </div>
      </section>

      {reorderSuggestions.length > 0 ? (
        <section className="panel-card">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Reposición</span>
              <h2>SKU que requieren atención</h2>
            </div>
            <Link className="compact-button" to="/compras">
              Ir a Compras
            </Link>
          </div>

          <div className="inventory-reorder-grid">
            {reorderSuggestions.map((item) => (
              <article
                className="inventory-reorder-card"
                key={item.warehouse_id + item.catalog_item_id}
              >
                <span
                  className={
                    Number(item.available_quantity) <= 0
                      ? "status-badge status-badge-cancelled"
                      : "status-badge status-badge-expired"
                  }
                >
                  {Number(item.available_quantity) <= 0
                    ? "Sin stock"
                    : "Stock bajo"}
                </span>
                <strong>{item.item_name}</strong>
                <span>
                  {item.sku ?? "Sin SKU"} · {item.warehouse_code}
                </span>
                <dl>
                  <div>
                    <dt>Disponible</dt>
                    <dd>{Number(item.available_quantity)}</dd>
                  </div>
                  <div>
                    <dt>Mínimo</dt>
                    <dd>{Number(item.minimum_quantity)}</dd>
                  </div>
                  <div>
                    <dt>Sugerido</dt>
                    <dd>{Number(item.suggested_quantity)}</dd>
                  </div>
                </dl>
                <small>
                  {item.supplier_name
                    ? `Proveedor: ${item.supplier_name}`
                    : "Sin proveedor asociado"}
                </small>
                {item.supplier_id ? (
                  <button
                    className="compact-button"
                    type="button"
                    disabled={quoteBusyKey === item.warehouse_id + ":" + item.catalog_item_id}
                    onClick={() => void requestSupplierQuote(item)}
                  >
                    {quoteBusyKey === item.warehouse_id + ":" + item.catalog_item_id
                      ? "Creando…"
                      : "Solicitar cotización"}
                  </button>
                ) : null}
              </article>
            ))}
          </div>
        </section>
      ) : null}

      {formOpen ? (
        <section className="panel-card">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Movimiento manual</span>
              <h2>Actualizar existencias</h2>
            </div>
          </div>

          <form className="inventory-movement-form" onSubmit={handleMovement}>
            <label>
              Almacén
              <select
                required
                value={warehouseId}
                onChange={(event) => setWarehouseId(event.target.value)}
              >
                <option value="">Seleccionar</option>
                {warehouses.map((warehouse) => (
                  <option value={warehouse.id} key={warehouse.id}>
                    {warehouse.name} · {warehouse.code}
                  </option>
                ))}
              </select>
            </label>

            <label className="field-span-2">
              Producto
              <select
                required
                value={itemId}
                onChange={(event) => {
                  const nextId = event.target.value;
                  setItemId(nextId);
                  const item = items.find((option) => option.id === nextId);
                  setUnitCost(item ? String(item.cost_price ?? "") : "");
                }}
              >
                <option value="">Seleccionar</option>
                {items.map((item) => (
                  <option value={item.id} key={item.id}>
                    {item.name}{item.sku ? ` · ${item.sku}` : ""}
                  </option>
                ))}
              </select>
            </label>

            <label>
              Tipo
              <select
                value={movementType}
                onChange={(event) =>
                  setMovementType(event.target.value as MovementType)
                }
              >
                {Object.entries(MOVEMENT_LABELS).map(([value, label]) => (
                  <option value={value} key={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>

            <label>
              Cantidad
              <input
                type="number"
                min="0.001"
                step="0.001"
                required
                value={quantity}
                onChange={(event) => setQuantity(event.target.value)}
              />
            </label>

            <label>
              Costo unitario
              <input
                type="number"
                min="0"
                step="0.0001"
                value={unitCost}
                onChange={(event) => setUnitCost(event.target.value)}
              />
            </label>

            <label className="field-span-2">
              Nota
              <input
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                placeholder="Motivo o referencia interna"
              />
            </label>

            <div className="form-actions field-span-full">
              <button className="primary-button" type="submit" disabled={saving}>
                {saving ? "Registrando…" : "Registrar movimiento"}
              </button>
            </div>
          </form>
        </section>
      ) : null}

      {actionError ? (
        <div className="inline-alert" role="alert">
          {actionError}
        </div>
      ) : null}

      {actionSuccess ? (
        <div className="form-success inventory-message">
          {actionSuccess}
        </div>
      ) : null}

      <section className="toolbar-card">
        <div>
          <strong>{loading ? "…" : rows.length}</strong>
          <span> posiciones de inventario</span>
        </div>
        <input
          type="search"
          placeholder="Buscar por producto, SKU o almacén"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </section>

      {loadError ? (
        <div className="inline-alert" role="alert">
          {loadError}
        </div>
      ) : null}

      {!loading && rows.length === 0 && !loadError ? (
        <section className="empty-state">
          <strong>No hay productos inventariables para mostrar.</strong>
          <p>Activa productos con control de inventario en el catálogo.</p>
        </section>
      ) : null}

      <section className="inventory-grid" aria-label="Stock por almacén">
        {rows.map((row) => {
          const key = `${row.warehouse_id}:${row.catalog_item_id}`;
          const minimumValue =
            minimumInputs[key] ?? String(row.minimum_quantity);

          return (
            <article
              className={
                row.low_stock
                  ? "inventory-card inventory-card-low"
                  : "inventory-card"
              }
              key={key}
            >
              <div className="inventory-card-heading">
                <div>
                  <span
                    className={
                      row.low_stock
                        ? "status-badge status-badge-expired"
                        : "status-badge"
                    }
                  >
                    {row.low_stock ? "Stock bajo" : row.warehouse_code}
                  </span>
                  <h2>{row.item_name}</h2>
                  <p>
                    {row.sku ?? "Sin SKU"} · {row.warehouse_name}
                  </p>
                </div>
                <strong>{number(row.available_quantity).toFixed(3)}</strong>
              </div>

              <dl className="customer-meta">
                <div>
                  <dt>Existencia</dt>
                  <dd>{number(row.quantity).toFixed(3)}</dd>
                </div>
                <div>
                  <dt>Reservado</dt>
                  <dd>{number(row.reserved_quantity).toFixed(3)}</dd>
                </div>
                <div>
                  <dt>Costo promedio</dt>
                  <dd>{money(row.average_cost)}</dd>
                </div>
                <div>
                  <dt>Valor</dt>
                  <dd>{money(row.stock_value)}</dd>
                </div>
              </dl>

              <div className="minimum-stock-control">
                <label>
                  Stock mínimo
                  <input
                    type="number"
                    min="0"
                    step="0.001"
                    value={minimumValue}
                    onChange={(event) =>
                      setMinimumInputs((current) => ({
                        ...current,
                        [key]: event.target.value,
                      }))
                    }
                  />
                </label>
                <button
                  className="compact-button"
                  type="button"
                  disabled={minimumBusyKey === key}
                  onClick={() => void saveMinimum(row)}
                >
                  {minimumBusyKey === key ? "Guardando…" : "Guardar mínimo"}
                </button>
              </div>

              <div className="quotation-actions">
                <Link
                  className="compact-button"
                  to={`/inventario/${row.catalog_item_id}`}
                >
                  Ver detalle e historial
                </Link>
              </div>
            </article>
          );
        })}
      </section>
    </div>
  );
}
